// src/App.js (corrected - removed nested BrowserRouter)
import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Link, useLocation, useNavigate, Routes, Route } from 'react-router-dom';
import { Menu, X, Moon, Sun } from 'lucide-react';
import CookieConsent from './components/CookieConsent';
import { useCookieConsent } from './context/CookieConsentContext';

// Eager load Home for LCP
import Home from './pages/Home';

// Lazy load all other pages
const About = lazy(() => import('./pages/About'));
const Checkout = lazy(() => import('./pages/Checkout'));
const Cybersecurity = lazy(() => import('./pages/Cybersecurity'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Team = lazy(() => import('./pages/Team'));
const AppliedDeepLearning = lazy(() => import('./pages/AppliedDeepLearning'));
const Company = lazy(() => import('./pages/Company'));
const Documentation = lazy(() => import('./pages/Documentation'));
const OpenSourceSolutions = lazy(() => import('./pages/OpenSourceSolutions'));
const Products = lazy(() => import('./pages/Products'));
const Technology = lazy(() => import('./pages/Technology'));
const Careers = lazy(() => import('./pages/Careers'));
const Contact = lazy(() => import('./pages/Contact'));
const EmbeddedSystems = lazy(() => import('./pages/EmbeddedSystems'));
const Partners = lazy(() => import('./pages/Partners'));
const Solutions = lazy(() => import('./pages/Solutions'));
const Terms = lazy(() => import('./pages/Terms'));
const CaseStudies = lazy(() => import('./pages/CaseStudies'));
const Cookies = lazy(() => import('./pages/Cookies'));
const Expertise = lazy(() => import('./pages/Expertise'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Support = lazy(() => import('./pages/Support'));
const Disclaimer = lazy(() => import('./pages/Disclaimer'));
const Accessibility = lazy(() => import('./pages/Accessibility'));
const ShippingReturns = lazy(() => import('./pages/ShippingReturns'));
const Research = lazy(() => import('./pages/Research'));
const NotFound = lazy(() => import('./pages/NotFound'));

export default function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });
  const [scrollY, setScrollY] = useState(0);

  // Cookie consent management
  const {
    showBanner,
    setShowBanner,
    acceptAll,
    acceptNecessary,
    acceptSelected,
  } = useCookieConsent();

  // Save dark mode preference to localStorage
  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    let viewportMeta = document.querySelector('meta[name="viewport"]');
    if (!viewportMeta) {
      viewportMeta = document.createElement('meta');
      viewportMeta.name = 'viewport';
      document.head.appendChild(viewportMeta);
    }
    viewportMeta.content = 'width=device-width, initial-scale=1.0, user-scalable=yes';

    let favicon = document.querySelector('link[rel="icon"]');
    if (!favicon) {
      favicon = document.createElement('link');
      favicon.rel = 'icon';
      document.head.appendChild(favicon);
    }
    favicon.href = darkMode
      ? "/cl_rebarnd_dark.svg"
      : "/cl_rebrand_light.svg";

    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [darkMode]);

  const styleSheet = `
    @keyframes scroll-left {
      from { transform: translateX(0%); }
      to { transform: translateX(-50%); }
    }
    .animate-scroll-left {
      animation: scroll-left 60s linear infinite;
    }
    
    .dark .footer-link-override, .footer-black .footer-link-override {
        color: #9ca3af !important;
        transition: color 0.2s;
    }
    .dark .footer-link-override:hover, .footer-black .footer-link-override:hover {
        color: #ffffff !important;
    }
    .light .footer-link-override, .footer-white .footer-link-override {
        color: #6b7280 !important;
    }
    .light .footer-link-override:hover, .footer-white .footer-link-override:hover {
        color: #000000 !important;
    }
    .nav-link-force-white a {
      color: #ffffff !important;
      font-weight: 300;
    }
    .nav-link-force-dark a {
      color: #111827 !important;
      font-weight: 300;
    }
    .nav-link-force-white a:hover, .nav-link-force-dark a:hover {
        color: #6b7280 !important;
    }
    .max-w-wider {
        max-width: 1400px;
    }
    
    .glass-morph {
      background: rgba(255, 255, 255, 0.02);
      border: 1px solid rgba(255, 255, 255, 0.05);
      transition: all 0.2s ease;
    }
    .dark .glass-morph {
      background: rgba(255, 255, 255, 0.02);
      border: 1px solid rgba(255, 255, 255, 0.05);
    }
    .light .glass-morph {
      background: rgba(0, 0, 0, 0.01);
      border: 1px solid rgba(0, 0, 0, 0.05);
    }
    .glass-morph:hover {
      transform: translateY(-2px);
      border-color: rgba(0, 0, 0, 0.1);
    }
    .dark .glass-morph:hover {
      border-color: rgba(255, 255, 255, 0.1);
    }
  `;

  return (
    <div className={`min-h-screen font-sans transition-colors duration-500 ${darkMode ? 'dark bg-black text-gray-300' : 'light bg-white text-gray-900'}`}>
      <style dangerouslySetInnerHTML={{ __html: styleSheet }} />
      
      {/* Header */}
      <Header darkMode={darkMode} setDarkMode={setDarkMode} mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} scrollY={scrollY} />

      {/* Main Content with Routes */}
      <main>
        <Suspense fallback={<div className={`min-h-screen ${darkMode ? 'bg-black' : 'bg-white'}`} />}>
          <Routes>
            <Route path="/" element={<Home darkMode={darkMode} />} />
            <Route path="/about" element={<About darkMode={darkMode} />} />
            <Route path="/checkout" element={<Checkout darkMode={darkMode} />} />
            <Route path="/cybersecurity" element={<Cybersecurity darkMode={darkMode} />} />
            <Route path="/product-detail" element={<ProductDetail darkMode={darkMode} />} />
            <Route path="/team" element={<Team darkMode={darkMode} />} />
            <Route path="/solutions/applied-deep-learning" element={<AppliedDeepLearning darkMode={darkMode} />} />
            <Route path="/company" element={<Company darkMode={darkMode} />} />
            <Route path="/documentation" element={<Documentation darkMode={darkMode} />} />
            <Route path="/solutions/opensource" element={<OpenSourceSolutions darkMode={darkMode} />} />
            <Route path="/products" element={<Products darkMode={darkMode} />} />
            <Route path="/technology" element={<Technology darkMode={darkMode} />} />
            <Route path="/careers" element={<Careers darkMode={darkMode} />} />
            <Route path="/contact" element={<Contact darkMode={darkMode} />} />
            <Route path="/solutions/embedded-systems" element={<EmbeddedSystems darkMode={darkMode} />} />
            <Route path="/partners" element={<Partners darkMode={darkMode} />} />
            <Route path="/solutions" element={<Solutions darkMode={darkMode} />} />
            <Route path="/terms" element={<Terms darkMode={darkMode} />} />
            <Route path="/case-studies" element={<CaseStudies darkMode={darkMode} />} />
            <Route path="/cookies" element={<Cookies darkMode={darkMode} />} />
            <Route path="/expertise" element={<Expertise darkMode={darkMode} />} />
            <Route path="/privacy" element={<Privacy darkMode={darkMode} />} />
            <Route path="/support" element={<Support darkMode={darkMode} />} />
            <Route path="/disclaimer" element={<Disclaimer darkMode={darkMode} />} />
            <Route path="/accessibility" element={<Accessibility darkMode={darkMode} />} />
            <Route path="/shipping-returns" element={<ShippingReturns darkMode={darkMode} />} />
            <Route path="/research" element={<Research darkMode={darkMode} />} />
            <Route path="*" element={<NotFound darkMode={darkMode} />} />
          </Routes>
        </Suspense>
      </main>

      {/* Footer */}
      <Footer darkMode={darkMode} />

      {/* Cookie Consent Banner */}
      <CookieConsent
        darkMode={darkMode}
        showBanner={showBanner}
        onAcceptAll={acceptAll}
        onAcceptNecessary={acceptNecessary}
        onAcceptSelected={acceptSelected}
        onClose={() => setShowBanner(false)}
      />
    </div>
  );
}

// Header component
function Header({ darkMode, setDarkMode, mobileMenuOpen, setMobileMenuOpen, scrollY }) {
  const location = useLocation();
  const navigate = useNavigate();

  // Handle scroll navigation and hash links
  useEffect(() => {
    // Handle state-based scroll
    if (location.state?.scrollTo) {
      const hash = location.state.scrollTo;
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
      window.history.replaceState({}, document.title);
    }
    // Handle hash in URL
    if (location.hash) {
      setTimeout(() => {
        const element = document.getElementById(location.hash.replace('#', ''));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location]);

  const handlePortfolioClick = (e) => {
    e.preventDefault();
    if (location.pathname === '/') {
      const element = document.getElementById('portfolio');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/', { state: { scrollTo: 'portfolio' } });
    }
  };

  return (
    <header className={`border-b sticky top-0 z-50 transition-all duration-500 backdrop-blur-md ${scrollY > 50 ? darkMode ? 'bg-black/90 border-gray-800 shadow-lg' : 'bg-white/90 border-gray-200 shadow-lg' : darkMode ? 'bg-black border-gray-800' : 'bg-white border-gray-200'}`}>
      <div className="max-w-wider mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <Link
            to="/"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center space-x-3 group cursor-pointer"
          >
            <img
              src={darkMode ? "/cl_rebarnd_dark.svg" : "/cl_rebrand_light.svg"}
              alt="CorrelationLab Logo"
              className="w-10 h-10 transform group-hover:scale-110 transition-transform duration-300"
            />
            <span className="text-2xl font-bold">CorrelationLab</span>
          </Link>
          <nav className={`hidden md:flex items-center space-x-8 ${darkMode ? 'nav-link-force-white' : 'nav-link-force-dark'}`}>
            <Link to="/about" className="transition-all hover:scale-105 hover:opacity-60">About</Link>
            <a href="/#portfolio" onClick={handlePortfolioClick} className="transition-all hover:scale-105 hover:opacity-60 cursor-pointer">Portfolio</a>
            <Link to="/careers" className="transition-all hover:scale-105 hover:opacity-60">Careers</Link>
            <a href="https://extheon.com" target="_blank" rel="noopener noreferrer" className="transition-all hover:scale-105 hover:opacity-60">Extheon</a>
            <button
              onClick={() => setDarkMode(!darkMode)}
              className={`p-2 rounded-lg transition-all border ${darkMode ? 'bg-white border-white text-black hover:bg-gray-100' : 'bg-black border-black text-white hover:bg-gray-900'}`}
              aria-label="Toggle dark mode"
            >
              {darkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            <Link to="/contact" className={`px-6 py-2 transition-all duration-300 font-medium ${darkMode ? 'bg-white text-black hover:bg-gray-200' : 'bg-black text-white hover:bg-gray-800'}`}>Contact Us</Link>
          </nav>

          <button
            className="md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle mobile menu"
            style={{
              background: 'transparent',
              border: 'none',
              padding: '0',
              cursor: 'pointer',
              appearance: 'none',
              WebkitAppearance: 'none'
            }}
          >
            {mobileMenuOpen ? <X size={24} color={darkMode ? 'white' : '#111827'} /> : <Menu size={24} color={darkMode ? 'white' : '#111827'} />}
          </button>
        </div>
      </div>
      {mobileMenuOpen && (
        <div className={`md:hidden border-t transition-colors ${darkMode ? 'border-gray-800 bg-black/95 backdrop-blur-md' : 'border-gray-200 bg-white/95 backdrop-blur-md'}`}>
          <nav className={`flex flex-col p-6 space-y-4 ${darkMode ? 'nav-link-force-white' : 'nav-link-force-dark'}`}>
            <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="hover:opacity-60">About</Link>
            <a href="/#portfolio" onClick={(e) => { handlePortfolioClick(e); setMobileMenuOpen(false); }} className="hover:opacity-60 cursor-pointer">Portfolio</a>
            <Link to="/careers" onClick={() => setMobileMenuOpen(false)} className="hover:opacity-60">Careers</Link>
            <a href="https://extheon.com" target="_blank" rel="noopener noreferrer" className="hover:opacity-60">Extheon</a>
            <button onClick={() => setDarkMode(!darkMode)} className={`flex items-center space-x-2 p-2 rounded-lg transition-all border ${darkMode ? 'bg-white border-white text-black hover:bg-gray-100' : 'bg-black border-black text-white hover:bg-gray-900'}`}>
              {darkMode ? <><Sun size={20} /><span>Light Mode</span></> : <><Moon size={20} /><span>Dark Mode</span></>}
            </button>
            <Link to="/contact" onClick={() => setMobileMenuOpen(false)} className={`px-6 py-2 w-full text-center font-medium ${darkMode ? 'bg-white text-black hover:bg-gray-200' : 'bg-black text-white hover:bg-gray-800'}`}>Contact Us</Link>
          </nav>
        </div>
      )}
    </header>
  );
}

// Footer component - Corporate Holding Company Style
function Footer({ darkMode }) {
  const logoRef = React.useRef(null);
  const hasAnimatedRef = React.useRef(false);
  const [isRotating, setIsRotating] = React.useState(false);

  // Scroll into view detection
  React.useEffect(() => {
    const logo = logoRef.current;
    if (!logo) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimatedRef.current) {
            hasAnimatedRef.current = true;
            setIsRotating(true);
            setTimeout(() => setIsRotating(false), 1300);
          }
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(logo);
    return () => observer.disconnect();
  }, []);

  // Reset animation flag when navigating away and back
  React.useEffect(() => {
    hasAnimatedRef.current = false;
  }, []);

  const handleLogoInteraction = () => {
    if (!isRotating) {
      setIsRotating(true);
      setTimeout(() => setIsRotating(false), 1300);
    }
  };

  return (
    <footer className={`py-20 transition-colors duration-500 border-t ${darkMode ? 'bg-black border-gray-900 dark footer-black' : 'bg-white border-gray-100 light footer-white'}`}>
      <div className="max-w-wider mx-auto px-6">
        <div className="grid lg:grid-cols-5 gap-12 mb-16">
          {/* Logo and Description */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-3 mb-6" id="footer-logo">
              <img
                ref={logoRef}
                src={darkMode ? "/cl_rebarnd_dark.svg" : "/cl_rebrand_light.svg"}
                alt="CorrelationLab Logo"
                className="w-8 h-8 cursor-pointer transition-transform duration-300 ease-in-out hover:rotate-45"
                style={{ transform: isRotating ? 'rotate(45deg)' : undefined }}
                tabIndex={0}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  handleLogoInteraction();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleLogoInteraction();
                }}
              />
              <span className={`text-xl font-light ${darkMode ? 'text-white' : 'text-gray-900'}`}>CorrelationLab</span>
            </div>
            <p className={`text-sm font-light leading-relaxed ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Deep technology research.
            </p>
          </div>

          {/* Links - 3 column on mobile, part of 5-col grid on lg */}
          <div className="lg:col-span-3 grid grid-cols-3 gap-8">
            {/* Company */}
            <div>
              <div className={`font-medium mb-5 text-xs tracking-wider uppercase ${darkMode ? 'text-gray-500' : 'text-gray-500'}`} role="heading" aria-level="2">Company</div>
              <ul className="space-y-3 text-sm font-light">
                <li><Link to="/about" className={`hover:opacity-70 transition-opacity ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>About</Link></li>
                <li><Link to="/research" className={`hover:opacity-70 transition-opacity ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Research</Link></li>
                <li><Link to="/careers" className={`hover:opacity-70 transition-opacity ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Careers</Link></li>
                <li><Link to="/contact" className={`hover:opacity-70 transition-opacity ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Contact</Link></li>
              </ul>
            </div>

            {/* Ventures */}
            <div>
              <div className={`font-medium mb-5 text-xs tracking-wider uppercase ${darkMode ? 'text-gray-500' : 'text-gray-500'}`} role="heading" aria-level="2">Ventures</div>
              <ul className="space-y-3 text-sm font-light">
                <li><a href="/#portfolio" className={`hover:opacity-70 transition-opacity cursor-pointer ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Overview</a></li>
                <li><a href="https://extheon.com" target="_blank" rel="noopener noreferrer" className={`hover:opacity-70 transition-opacity ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Extheon</a></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <div className={`font-medium mb-5 text-xs tracking-wider uppercase ${darkMode ? 'text-gray-500' : 'text-gray-500'}`} role="heading" aria-level="2">Legal</div>
              <ul className="space-y-3 text-sm font-light">
                <li><Link to="/privacy" className={`hover:opacity-70 transition-opacity ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Privacy</Link></li>
                <li><Link to="/terms" className={`hover:opacity-70 transition-opacity ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Terms</Link></li>
                <li><Link to="/cookies" className={`hover:opacity-70 transition-opacity ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Cookies</Link></li>
                <li><Link to="/disclaimer" className={`hover:opacity-70 transition-opacity ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Disclaimer</Link></li>
                <li><Link to="/accessibility" className={`hover:opacity-70 transition-opacity ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Accessibility</Link></li>
                <li><Link to="/shipping-returns" className={`hover:opacity-70 transition-opacity ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Shipping & Returns</Link></li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className={`border-t pt-8 ${darkMode ? 'border-gray-900' : 'border-gray-100'}`}>
          <div className={`flex flex-col md:flex-row justify-between items-start md:items-center gap-4 text-xs font-light ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
            <div>
              © 2023-2026 CorrelationLab Scientific Research LLP. All rights reserved.
            </div>
            <div className={`text-right ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              <div>LLPIN: ACC-1204 · Kollam, Kerala</div>
              <div>GSTIN: 32AASFC9416G1ZB</div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
